import React from 'react';
import { cn } from '@/lib/utils';
import CalendarDatePicker, {
  birthDateRange,
  type CalendarDatePickerSize,
} from './CalendarDatePicker';

export interface DatePickerProps {
  label?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  /** ISO date string (YYYY-MM-DD), or '' for no date. */
  value?: string;
  onChange?: (value: string) => void;
  /** Selectable range, inclusive, as ISO dates. Defaults to a century back, a decade forward. */
  min?: string;
  max?: string;
  /**
   * A date of birth: 120 years back, nothing in the future, newest year first in the
   * year dropdown. Explicit `min`/`max` still win if given.
   */
  birthDate?: boolean;
  /** Year dropdown order. Defaults to newest-first for `birthDate`, oldest-first otherwise. */
  yearOrder?: 'asc' | 'desc';
  /**
   * `md` matches `form-input-base`; `sm` is for toolbars and inline table rows; `xs` is
   * the filter-sidebar size, whose calendar opens small enough to fit the sidebar.
   */
  size?: CalendarDatePickerSize;
  disabled?: boolean;
  placeholder?: string;
  /**
   * Render an inline clear control while a value is set, so an optional date visibly
   * reads as optional (SCRUM-1466). The calendar panel always offers Clear regardless,
   * and emptying the typed text clears too.
   */
  clearable?: boolean;
  /** Classes for the bordered field box (width, theme). */
  className?: string;
  /** Classes for the text input itself (text colour on a dark host, for instance). */
  inputClassName?: string;
  wrapperClassName?: string;
  id?: string;
  name?: string;
  /**
   * Accessible name when the field has no `label` of its own and no external
   * <label htmlFor>. Ignored when `label` is given, because an aria-label attribute
   * would outrank the label element and hide it from assistive tech.
   */
  ariaLabel?: string;
}

/**
 * Date picker — the shared `CalendarDatePicker` (typed MM/DD/YYYY plus a month/year
 * dropdown calendar) with a label, hint and error, so a form field reads like `Input`.
 *
 * This used to wrap a native <input type="date">. Every call site kept the same props
 * and the same ISO 'YYYY-MM-DD' value shape when it switched; the CalendarDatePicker
 * header lists what the native control cost us.
 */
export const DatePicker = React.forwardRef<HTMLInputElement, DatePickerProps>(
  (
    {
      label,
      error,
      hint,
      required,
      value,
      onChange,
      min,
      max,
      birthDate,
      yearOrder,
      size,
      disabled,
      className,
      inputClassName,
      wrapperClassName,
      id,
      name,
      placeholder,
      clearable,
      ariaLabel,
    },
    ref,
  ) => {
    const inputId = id ?? (label ? label.toLowerCase().replace(/\s+/g, '-') : undefined);
    const errorId = error && inputId ? `${inputId}-error` : undefined;
    const hintId = hint && !error && inputId ? `${inputId}-hint` : undefined;

    const range = birthDate ? birthDateRange() : undefined;

    return (
      <div className={cn('w-full', wrapperClassName)}>
        {label && (
          <label htmlFor={inputId} className="block text-sm font-medium text-slate-700 mb-1.5">
            {label}
            {required && (
              <span className="ml-0.5 text-red-500" aria-hidden="true">
                {' *'}
              </span>
            )}
          </label>
        )}

        <CalendarDatePicker
          inputRef={ref}
          id={inputId}
          name={name}
          value={value ?? ''}
          onChange={(next) => onChange?.(next)}
          min={min ?? range?.min}
          max={max ?? range?.max}
          yearOrder={yearOrder ?? (birthDate ? 'desc' : 'asc')}
          size={size}
          disabled={disabled}
          required={required}
          invalid={!!error}
          clearable={clearable}
          placeholder={placeholder}
          ariaLabel={label ? undefined : ariaLabel}
          ariaDescribedBy={errorId ?? hintId}
          className={className}
          inputClassName={inputClassName}
        />

        {hint && !error && (
          <p id={hintId} className="mt-1.5 text-xs text-slate-500">
            {hint}
          </p>
        )}
        {error && (
          <p id={errorId} role="alert" className="mt-1.5 text-xs text-red-600">
            {error}
          </p>
        )}
      </div>
    );
  },
);

DatePicker.displayName = 'DatePicker';
